import { useEffect, useState } from "react";
import { Terminal, ChevronDown } from "lucide-react";
import { api, eur } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const EXPECTED_FALLBACK = 45;

export default function DiagnosticsConsole() {
  const { t, lang } = useLang();
  const [lines, setLines] = useState([]);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("running");

  useEffect(() => {
    const started = performance.now();
    const stamp = () => `+${String(Math.round(performance.now() - started)).padStart(4, "0")}ms`;
    const out = [];
    const log = (level, msg) => out.push({ level, msg, at: stamp() });
    let cancelled = false;

    (async () => {
      log("info", t("diag.boot"));
      try {
        const { data } = await api.get("/shipping/health");
        const ok = Math.abs(Number(data.fallback_rate) - EXPECTED_FALLBACK) < 0.005;
        log(ok ? "ok" : "warn", `${t("diag.fallback")} ${eur(data.fallback_rate)} — ${ok ? t("diag.secure") : `${t("diag.anomaly")} ${eur(EXPECTED_FALLBACK)}`}`);
        log(data.handling_fee > 0 ? "ok" : "warn", `${t("diag.handling")} ${eur(data.handling_fee)}`);
        log(data.ups_configured ? "ok" : "info", `${t("diag.ups")} ${data.ups_configured ? `${t("diag.connected")} (${data.ups_env.toUpperCase()})` : t("diag.upsFallback")}`);
        log("info", `${t("diag.stripe")} ${data.stripe_mode.toUpperCase()} · ${data.currency}`);
        log(ok ? "ok" : "warn", ok ? t("diag.done") : t("diag.doneWarn"));
        if (!cancelled) setStatus(ok ? "ok" : "warn");
      } catch {
        log("err", t("diag.unreachable"));
        if (!cancelled) setStatus("err");
      }
      if (!cancelled) setLines([...out]);
    })();
    return () => { cancelled = true; };
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const color = { ok: "text-brass", warn: "text-amber-300", err: "text-red-300", info: "text-bone-dim", running: "text-bone-muted" };
  const tag = { ok: "[ OK ]", warn: "[WARN]", err: "[ERR ]", info: "[INFO]" };

  return (
    <div data-testid="diagnostics-console" className="border-t border-bone/10 bg-ink">
      <button data-testid="diagnostics-toggle" type="button" onClick={() => setOpen((o) => !o)} className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-3 font-mono text-[9px] tracking-[0.3em] text-bone-muted transition-colors hover:text-bone sm:px-10 lg:px-16">
        <span className="flex items-center gap-2">
          <Terminal size={11} /> {t("diag.title")}
          <span data-testid="diagnostics-status" className={`ml-2 ${color[status]}`}>
            {status === "running" ? "…" : status === "ok" ? t("diag.statusOk") : status === "warn" ? t("diag.statusWarn") : t("diag.statusErr")}
          </span>
        </span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <pre data-testid="diagnostics-log" className="mx-auto max-w-7xl overflow-x-auto whitespace-pre-wrap border-t border-bone/5 px-6 pb-5 pt-4 font-mono text-[10px] leading-relaxed sm:px-10 lg:px-16">
          {lines.map((l, i) => (
            <div key={i} data-testid={`diagnostics-line-${i}`} className={color[l.level]}>
              <span className="text-bone-muted">{l.at}</span> {tag[l.level]} {l.msg}
            </div>
          ))}
          {!lines.length && <div className="text-bone-muted">{t("diag.boot")}</div>}
        </pre>
      )}
    </div>
  );
}
